import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useSiteContent } from "@/contexts/SiteContentContext";
import { resolveAssetUrl } from "@/lib/api";

function PanelWrap({ linkUrl, index, children }) {
  const cls = "relative block h-full overflow-hidden group";
  if (linkUrl) {
    return (
      <a href={linkUrl} data-testid={`top-strip-panel-${index}`} className={cls}>
        {children}
      </a>
    );
  }
  return (
    <div data-testid={`top-strip-panel-${index}`} className={cls.replace("block ", "")}>
      {children}
    </div>
  );
}

function Panel({ panel, index }) {
  const videoRef = useRef(null);
  const [ready, setReady] = useState(false);
  const url = panel?.media_url || "";
  const captionImage = panel?.caption_image_url || "";
  const isVideo = url && (url.endsWith(".mp4") || url.endsWith(".webm") || url.endsWith(".mov"));

  useEffect(() => {
    if (!isVideo) return;
    const v = videoRef.current;
    if (!v) return;
    v.muted = true;
    v.playsInline = true;
    v.loop = true;
    v.src = resolveAssetUrl(url);
    const tryPlay = () => v.play().then(() => setReady(true)).catch(() => {});
    v.addEventListener("canplay", tryPlay);
    return () => v.removeEventListener("canplay", tryPlay);
  }, [url, isVideo]);

  const captionNode = captionImage ? (
    <img
      src={resolveAssetUrl(captionImage)}
      alt={panel?.caption || "brand"}
      className="h-12 md:h-16 lg:h-20 w-auto max-w-full object-contain drop-shadow-lg"
    />
  ) : panel?.caption ? (
    <span className="font-display font-black uppercase text-white text-base md:text-xl lg:text-2xl tracking-widest drop-shadow-lg">
      {panel.caption}
    </span>
  ) : null;

  if (!url) {
    return (
      <PanelWrap linkUrl={panel?.link_url} index={index}>
        <div className="absolute inset-0 bg-[#141414] flex items-center justify-center px-4">
          {captionNode || (
            <span className="font-display font-black uppercase text-white/40 text-base md:text-xl lg:text-2xl tracking-widest">—</span>
          )}
        </div>
      </PanelWrap>
    );
  }

  return (
    <PanelWrap linkUrl={panel?.link_url} index={index}>
      {isVideo ? (
        <video
          ref={videoRef}
          autoPlay
          muted
          loop
          playsInline
          poster={resolveAssetUrl(url)}
          className={`absolute inset-0 w-full h-full object-cover transition-all duration-500 group-hover:scale-105 ${ready ? "opacity-100" : "opacity-90"}`}
        />
      ) : (
        <img
          src={resolveAssetUrl(url)}
          alt={panel?.caption || ""}
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
      )}
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/60 pointer-events-none" />
      {captionNode && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none px-4">
          {captionNode}
        </div>
      )}
    </PanelWrap>
  );
}

export default function TopStrip() {
  const content = useSiteContent();
  const strip = content?.top_strip;
  if (!strip?.enabled) return null;
  const panels = strip.panels || [];
  const logoUrl = strip.logo_url || "/logo.png";

  // Layout: [panel 0] [panel 1] [LOGO] [panel 2] [panel 3]
  const left = [panels[0], panels[1]].filter(Boolean);
  const right = [panels[2], panels[3]].filter(Boolean);

  return (
    <div
      data-testid="top-strip"
      className="grid grid-cols-[repeat(2,minmax(0,1fr))_auto_repeat(2,minmax(0,1fr))] border-b border-white/10 bg-[#0A0A0A] h-20 md:h-28 lg:h-32"
    >
      {left.map((p, idx) => (
        <div key={`l-${idx}`} className="border-r border-white/10 overflow-hidden">
          <Panel panel={p} index={idx} />
        </div>
      ))}
      <Link
        to="/"
        data-testid="top-strip-logo"
        className="flex items-center justify-center px-3 md:px-6 lg:px-10 bg-[#0A0A0A] group"
        aria-label="Moto Petsas GP — Αρχική"
      >
        <img
          src={resolveAssetUrl(logoUrl)}
          alt="Moto Petsas GP"
          className="h-12 md:h-16 lg:h-20 w-auto max-w-full object-contain transition-transform duration-300 group-hover:scale-[1.03]"
        />
      </Link>
      {right.map((p, idx) => (
        <div key={`r-${idx}`} className={`overflow-hidden ${idx === 0 ? "border-l border-white/10 border-r border-white/10" : ""}`}>
          <Panel panel={p} index={idx + 2} />
        </div>
      ))}
    </div>
  );
}
